
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Tab,
    Tabs,
    TextField,
    Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import { baseUrl, userLocation } from "../Univ.js";


const CATEGORIES = [
    "Consulting",
    "Lab",
    "CT",
    "X-ray",
    "Scan / USG",
    "Dialysis",
    "Audiology",
    "Dental",
    "Minor",
];

const emptyAmounts = () => ({
    total: 0,
    paid: 0,
    balance: 0,
    discount: 0,
});

const toNumber = (value) => {
    const number = Number(value ?? 0);
    return Number.isFinite(number) ? number : 0;
};

const money = (value) =>
    toNumber(value).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const normalize = (value) =>
    String(value ?? "").trim().toLowerCase();

const getInvoiceType = (item) => {
    if (item.invoice) return "consulting";
    if (item.lab_invoice) return "lab";
    if (item.inv_invoice) return "inv";
    if (item.min_invoice) return "minor";
    return "unknown";
};

const getInvoiceNumber = (item) =>
    item.invoice ||
    item.lab_invoice ||
    item.inv_invoice ||
    item.min_invoice ||
    "";

const getAppointmentId = (item) => item.appt_id || "";

const getConsultant = (item) =>
    item["cons__consultant"] ||
    item["ip__primary_consult__consultant"] ||
    item["ip__refconsultant__consultant"] ||
    item.consultant ||
    "";

const getCategory = (item) => {
    /*
     * The investigation queryset should return the investigation IDs
     * and/or category names for correct CT, X-ray, Scan/USG, etc. splits.
     * Use available category information if the API provides it.
     */
    const category = normalize(
        item.category ||
        item.inv_category ||
        item.test_category ||
        item.component_category ||
        item.cat
    );

    if (category.includes("ct")) return "CT";

    if (
        category.includes("x-ray") ||
        category.includes("xray") ||
        category.includes("radiograph")
    ) {
        return "X-ray";
    }

    if (
        category.includes("usg") ||
        category.includes("ultrasound") ||
        category.includes("scan")
    ) {
        return "Scan / USG";
    }

    if (category.includes("dialysis")) return "Dialysis";
    if (category.includes("audio")) return "Audiology";
    if (category.includes("dental")) return "Dental";

    return null;
};

const getAmounts = (item, type) => {
    if (type === "consulting") {
        const paid = toNumber(item.paid);
        const balance = toNumber(item.balance);

        return {
            total: paid + balance,
            paid,
            balance,
            discount: 0,
        };
    }

    if (type === "lab") {
        return {
            total: toNumber(item.lab_tot),
            paid: toNumber(item.lab_paid),
            balance: toNumber(item.lab_balance),
            discount: toNumber(item.lab_disc),
        };
    }

    if (type === "inv") {
        return {
            total: toNumber(item.inv_tot),
            paid: toNumber(item.inv_paid),
            balance: toNumber(item.inv_balance),
            discount: toNumber(item.inv_disc),
        };
    }

    if (type === "minor") {
        return {
            total: toNumber(item.min_tot),
            paid: toNumber(item.min_paid),
            balance: toNumber(item.min_balance),
            discount: toNumber(item.min_disc),
        };
    }

    return emptyAmounts();
};

const getInvoicePath = (item, type) => {
    const invoice = getInvoiceNumber(item);

    if (type === "consulting") return `/ip-invoice/${invoice}`;
    if (type === "lab") return `/lab-invoice-ip/${invoice}`;
    if (type === "inv") return `/inv-invoice-ip/${invoice}`;
    if (type === "minor") return `/min-invoice-ip/${invoice}`;

    return "#";
};

const buildRows = (items) =>
    items.map((item, index) => {
        const type = getInvoiceType(item);
        const amounts = getAmounts(item, type);
        const category = getCategory(item);

        const categoryAmounts = Object.fromEntries(
            CATEGORIES.map((name) => [name, emptyAmounts()])
        );

        if (type === "consulting") {
            categoryAmounts.Consulting = amounts;
        } else if (type === "lab") {
            categoryAmounts.Lab = amounts;
        } else if (type === "minor") {
            categoryAmounts.Minor = amounts;
        } else if (type === "inv" && category) {
            categoryAmounts[category] = amounts;
        }

        return {
            ...item,

            // IDs can repeat between separate billing tables.
            id: `${type}-${item.id ?? index}-${getInvoiceNumber(item)}`,

            sourceType: type,
            date: item.add_date_date || item.add_date || "",
            ipId: item["ip__ip_id"] || "",
            appointmentId: getAppointmentId(item),
            patientId: item["ip__pat__pat_id"] || "",
            patientName: item["ip__pat__patient"] || "",
            consultantName: getConsultant(item),
            cashierName: item.cashier || "",
            invoiceNumber: getInvoiceNumber(item),
            invoicePath: getInvoicePath(item, type),

            total: amounts.total,
            paid: amounts.paid,
            balance: amounts.balance,
            discount: amounts.discount,

            categoryAmounts,
        };
    });

const getReportAmount = (row, category, reportType) => {
    const amounts = row.categoryAmounts[category] || emptyAmounts();

    if (reportType === "invoiced") return amounts.total;
    if (reportType === "collection") return amounts.paid;
    if (reportType === "pending") return amounts.balance;
    if (reportType === "discount") return amounts.discount;

    return 0;
};

function PaymentDetailIp() {
    const today = new Date();
    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState(formatDate(today));
    const [cashierSearch, setCashierSearch] = useState("");
    const [consultantFilter, setConsultantFilter] = useState("all");
    const [modeFilter, setModeFilter] = useState("");
    const [reportType, setReportType] = useState("invoiced");

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError("");

        try {
            const response = await axios.get(
                `${baseUrl}all-ip-collection-view/`,
                {
                    params: {
                        start_date: startDate,
                        end_date: endDate,
                        cashier: "",
                        mode: "",
                        status: 2,
                        location: userLocation,
                    },
                }
            );

            const data = Array.isArray(response.data)
                ? response.data
                : [];

            setRows(buildRows(data));
        } catch (err) {
            console.error("Failed to fetch IP collection report:", err);
            setError(
                err.response?.data?.detail ||
                err.response?.data?.error ||
                "Unable to load the IP report. Please check the API and try again."
            );
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const consultants = useMemo(() => {
        const names = rows
            .map((row) => row.consultantName)
            .filter(Boolean);

        return [...new Set(names)].sort((a, b) =>
            a.localeCompare(b)
        );
    }, [rows]);

    const filteredRows = useMemo(() => {
        const cashierQuery = normalize(cashierSearch);

        return rows.filter((row) => {
            const matchesCashier =
                !cashierQuery ||
                normalize(row.cashierName).includes(cashierQuery);

            const matchesConsultant =
                consultantFilter === "all" ||
                normalize(row.consultantName) ===
                    normalize(consultantFilter);

            const matchesMode =
                !modeFilter ||
                normalize(
                    row.mode ||
                    row.lab_pmode ||
                    row.inv_pmode ||
                    row.min_pmode
                ) === normalize(modeFilter);

            return (
                matchesCashier &&
                matchesConsultant &&
                matchesMode
            );
        });
    }, [rows, cashierSearch, consultantFilter, modeFilter]);

    const totals = useMemo(() => {
        const result = Object.fromEntries(
            CATEGORIES.map((category) => [
                category,
                { total: 0, count: 0 },
            ])
        );

        filteredRows.forEach((row) => {
            CATEGORIES.forEach((category) => {
                result[category].total += getReportAmount(
                    row,
                    category,
                    reportType
                );

                if (
                    getReportAmount(row, category, reportType) !== 0
                ) {
                    result[category].count += 1;
                }
            });
        });

        result.grandTotal = CATEGORIES.reduce(
            (sum, category) => sum + result[category].total,
            0
        );

        return result;
    }, [filteredRows, reportType]);

    const reportLabel = {
        invoiced: "Invoiced",
        collection: "Collected",
        pending: "Pending Dues",
        discount: "Discount",
    }[reportType];

    const columns = useMemo(() => {
        const baseColumns = [
            {
                field: "date",
                headerName: "Date",
                width: 112,
            },
            {
                field: "ipId",
                headerName: "IP ID",
                width: 135,
            },
            {
                field: "appointmentId",
                headerName: "Appointment ID",
                width: 150,
            },
            {
                field: "patientId",
                headerName: "Patient ID",
                width: 115,
            },
            {
                field: "patientName",
                headerName: "Patient Name",
                width: 165,
            },
            {
                field: "consultantName",
                headerName: "Consultant",
                width: 165,
                valueGetter: (value, row) =>
                    row.consultantName || "—",
            },
            {
                field: "invoiceNumber",
                headerName: "Invoice / Receipt",
                width: 170,
                sortable: false,
                renderCell: (params) =>
                    params.row.invoiceNumber ? (
                        <Link
                            to={params.row.invoicePath}
                            className="payment-ip-invoice-link"
                        >
                            {params.row.invoiceNumber}
                        </Link>
                    ) : (
                        "—"
                    ),
            },
        ];

        const categoryColumns = CATEGORIES.map((category) => ({
            field: `category_${category}`,
            headerName: `${category} ${
                reportType === "invoiced"
                    ? "Total"
                    : reportType === "collection"
                    ? "Collected"
                    : reportType === "pending"
                    ? "Balance"
                    : "Discount"
            }`,
            width: 145,
            type: "number",
            valueGetter: (_value, row) =>
                getReportAmount(row, category, reportType),
            renderCell: (params) => money(params.value),
        }));

        return [
            ...baseColumns,
            ...categoryColumns,
            {
                field: "cashierName",
                headerName: "Cashier",
                width: 130,
            },
        ];
    }, [reportType]);

    const clearFilters = () => {
        setCashierSearch("");
        setConsultantFilter("all");
        setModeFilter("");
    };

    return (
        <Box className="payment-ip-page">
            <Box className="payment-ip-header">
                <Box>
                    <Typography variant="h5" className="payment-ip-title">
                        IP Invoiced & Collection Report
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Inpatient billing, collections, consultant and
                        category-wise summaries
                    </Typography>
                </Box>

                <Button
                    variant="contained"
                    onClick={fetchData}
                    disabled={loading}
                >
                    Refresh
                </Button>
            </Box>

            <Card className="payment-ip-filter-card">
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label="Start Date"
                                value={startDate}
                                onChange={(e) =>
                                    setStartDate(e.target.value)
                                }
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label="End Date"
                                value={endDate}
                                onChange={(e) =>
                                    setEndDate(e.target.value)
                                }
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Search by Cashier"
                                placeholder="Enter cashier name"
                                value={cashierSearch}
                                onChange={(e) =>
                                    setCashierSearch(e.target.value)
                                }
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="ip-consultant-filter-label">
                                    Consultant
                                </InputLabel>
                                <Select
                                    labelId="ip-consultant-filter-label"
                                    label="Consultant"
                                    value={consultantFilter}
                                    onChange={(e) =>
                                        setConsultantFilter(e.target.value)
                                    }
                                >
                                    <MenuItem value="all">
                                        All Consultants
                                    </MenuItem>

                                    {consultants.map((consultant) => (
                                        <MenuItem
                                            key={consultant}
                                            value={consultant}
                                        >
                                            {consultant}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="ip-mode-filter-label">
                                    Payment Mode
                                </InputLabel>
                                <Select
                                    labelId="ip-mode-filter-label"
                                    label="Payment Mode"
                                    value={modeFilter}
                                    onChange={(e) =>
                                        setModeFilter(e.target.value)
                                    }
                                >
                                    <MenuItem value="">All Modes</MenuItem>
                                    <MenuItem value="cash">Cash</MenuItem>
                                    <MenuItem value="card">Card</MenuItem>
                                    <MenuItem value="upi">UPI</MenuItem>
                                    <MenuItem value="online">Online</MenuItem>
                                    <MenuItem value="advance">Advance</MenuItem>
                                    <MenuItem value="cheque">Cheque</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={2}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={clearFilters}
                            >
                                Clear Filters
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Card className="payment-ip-report-card">
                <Box className="payment-ip-tabs">
                    <Tabs
                        value={reportType}
                        onChange={(_event, value) => setReportType(value)}
                        variant="scrollable"
                        scrollButtons="auto"
                    >
                        <Tab label="Invoiced Report" value="invoiced" />
                        <Tab label="Collection Report" value="collection" />
                        <Tab label="Pending Dues" value="pending" />
                        <Tab label="Discount Report" value="discount" />
                    </Tabs>
                </Box>

                <CardContent>
                    <Box className="payment-ip-summary">
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                {reportLabel} Total
                            </Typography>
                            <Typography variant="h5" className="payment-ip-grand-total">
                                ₹{money(totals.grandTotal)}
                            </Typography>
                        </Box>

                        <Chip
                            label={`${filteredRows.length} records`}
                            color="primary"
                            variant="outlined"
                        />

                        {consultantFilter !== "all" && (
                            <Chip
                                label={`Consultant: ${consultantFilter}`}
                                color="success"
                                variant="outlined"
                            />
                        )}
                    </Box>

                    <Grid container spacing={1.5} className="payment-ip-category-cards">
                        {CATEGORIES.map((category) => (
                            <Grid item xs={6} sm={4} md={3} lg={2} key={category}>
                                <Box className="payment-ip-category-card">
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {category}
                                    </Typography>
                                    <Typography variant="h6">
                                        ₹{money(totals[category].total)}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>

                    {error && (
                        <Box className="payment-ip-error">
                            {error}
                        </Box>
                    )}

                    {loading ? (
                        <Box className="payment-ip-loading">
                            <CircularProgress size={32} />
                            <Typography variant="body2">
                                Loading IP report...
                            </Typography>
                        </Box>
                    ) : (
                        <Box className="payment-ip-grid">
                            <DataGrid
                                rows={filteredRows}
                                columns={columns}
                                getRowId={(row) => row.id}
                                autoHeight
                                pageSizeOptions={[10, 25, 50, 100]}
                                initialState={{
                                    pagination: {
                                        paginationModel: {
                                            page: 0,
                                            pageSize: 10,
                                        },
                                    },
                                }}
                                disableRowSelectionOnClick
                                density="compact"
                                sx={{
                                    border: 0,
                                    "& .MuiDataGrid-columnHeaders": {
                                        backgroundColor: "#f1f5f9",
                                        fontWeight: 700,
                                    },
                                    "& .MuiDataGrid-columnHeaderTitle": {
                                        fontWeight: 700,
                                    },
                                    "& .MuiDataGrid-row:hover": {
                                        backgroundColor: "#f8fafc",
                                    },
                                }}
                            />
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}

export default PaymentDetailIp;
